import { type Context, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import { fg } from '@atlaskit/platform-feature-flags';
import type { EditorContextPayloadData } from '@atlaskit/rovo-triggers/types';
import { useChatContextEditorContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store';

export enum PageContextType {
	ADMIN_HUB = 'admin-hub',
	BITBUCKET = 'bitbucket',
	CONFLUENCE_HOME = 'confluence-home',
	CONFLUENCE_PAGE_VIEW = 'confluence-page-view',
	CONFLUENCE_PAGE_EDIT = 'confluence-page-edit',
	CONFLUENCE_WHITEBOARD = 'confluence-whiteboard',
	JIRA_HOME = 'jira-home',
	JIRA_ISSUE_VIEW = 'jira-issue-view',
	JIRA_BACKLOG = 'jira-backlog',
	JIRA_BOARD = 'jira-board-view',
	JIRA_PROJECT = 'jira-project',
	JIRA_SERVICE_DESK = 'jira-service-desk',
	JIRA_PLAN = 'jira-plan',
	JIRA_CMP_SPACE_SETTINGS_PEOPLE = 'jira-cmp-space-settings-people',
	JIRA_ADMIN_PERMISSION_SCHEMES_VIEW = 'jira-admin-permission-schemes-view',
	JIRA_ADMIN_PERMISSION_SCHEMES_ADD = 'jira-admin-permission-schemes-add',
	JIRA_ADMIN_PERMISSION_SCHEMES_EDIT = 'jira-admin-permission-schemes-edit',
	JIRA_ADMIN_PERMISSION_SCHEMES_DELETE = 'jira-admin-permission-schemes-delete',
	ATLAS_HOME = 'atlas-home',
	ATLAS_PROJECT = 'atlas-project',
	ATLAS_GOAL = 'atlas-goal',
	FEEDBACK_APP = 'feedback-app',
	TRELLO_BOARD = 'trello-board',
	TRELLO_CARD = 'trello-card',
	UNKNOWN = 'unknown',
}

export const WindowLocationContext: Context<string | null> = createContext<string | null>(null);

export const useWindowLocation = (): string => {
	const [location, setLocation] = useState(window.location.href);
	// For easier mocking
	const contextValue = useContext(WindowLocationContext);

	const handleLocationChange = useCallback(() => {
		if (window.location.href !== location) {
			setLocation(window.location.href);
		}
	}, [location, setLocation]);

	useEffect(() => {
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function (...args) {
			originalPushState.apply(this, args);
			handleLocationChange();
		};

		window.history.replaceState = function (...args) {
			originalReplaceState.apply(this, args);
			handleLocationChange();
		};

		const unbind = bindAll(window, [
			{
				type: 'popstate',
				listener: handleLocationChange,
			},
		]);

		return () => {
			unbind();
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
		};
	}, [handleLocationChange]);

	return contextValue ?? location;
};

export enum UseCase {
	THINKING_VIEW = 'thinking-view',
	AUTOCOMPLETE = 'autocomplete',
}

const ALLOWED_PAGE_CONTEXT_TYPES: Record<UseCase, PageContextType[]> = {
	[UseCase.THINKING_VIEW]: [
		PageContextType.CONFLUENCE_PAGE_EDIT,
		PageContextType.CONFLUENCE_PAGE_VIEW,
		PageContextType.CONFLUENCE_WHITEBOARD,
		PageContextType.JIRA_ISSUE_VIEW,
		PageContextType.JIRA_SERVICE_DESK,
		PageContextType.JIRA_PROJECT,
		PageContextType.JIRA_PLAN,
		PageContextType.JIRA_CMP_SPACE_SETTINGS_PEOPLE,
		PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_VIEW,
		PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_ADD,
		PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_EDIT,
		PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_DELETE,
		PageContextType.ATLAS_PROJECT,
		PageContextType.ATLAS_GOAL,
		PageContextType.ADMIN_HUB,
		PageContextType.FEEDBACK_APP,
		PageContextType.TRELLO_BOARD,
		PageContextType.TRELLO_CARD,
	],
	[UseCase.AUTOCOMPLETE]: [
		PageContextType.BITBUCKET,
		PageContextType.CONFLUENCE_HOME,
		PageContextType.CONFLUENCE_PAGE_EDIT,
		PageContextType.CONFLUENCE_PAGE_VIEW,
		PageContextType.CONFLUENCE_WHITEBOARD,
		PageContextType.JIRA_BACKLOG,
		PageContextType.JIRA_HOME,
		PageContextType.JIRA_ISSUE_VIEW,
		PageContextType.ATLAS_HOME,
		PageContextType.ADMIN_HUB,
		PageContextType.TRELLO_BOARD,
		PageContextType.TRELLO_CARD,
	],
};

const FALLBACK_PAGE_CONTEXT_TYPE: Record<UseCase, PageContextType> = {
	[UseCase.THINKING_VIEW]: PageContextType.UNKNOWN,
	[UseCase.AUTOCOMPLETE]: PageContextType.ATLAS_HOME,
};

export const determinePageType = (
	location: string,
	useCase: UseCase = UseCase.THINKING_VIEW,
): PageContextType => {
	const allowedTypes = new Set(ALLOWED_PAGE_CONTEXT_TYPES[useCase]);

	const allUrlPatterns: [PageContextType, RegExp][] = [
		[PageContextType.BITBUCKET, /bitbucket\.org\/.*/],
		//If flag is on, use confluence page view for confluence home url pattern since model doesn't have enough data for home
		...(useCase === UseCase.AUTOCOMPLETE && fg('rovo_chat_typeahead_use_confluence-page_fallback')
			? [[PageContextType.CONFLUENCE_PAGE_VIEW, /\/wiki\/home$/] as [PageContextType, RegExp]]
			: [[PageContextType.CONFLUENCE_HOME, /\/wiki\/home$/] as [PageContextType, RegExp]]),
		[PageContextType.CONFLUENCE_PAGE_EDIT, /\/wiki\/spaces\/[^\/]+\/(?:pages|blog)\/edit-v2\//],
		[PageContextType.CONFLUENCE_PAGE_VIEW, /\/wiki\/spaces\/[^\/]+\/(?:pages|blog)\//],
		// Support whiteboard url
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/wiki\/spaces\/[^\/]+\/whiteboard\//],
		// Support whiteboard iframe url
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/whiteboards\/whiteboard\/[^\/\?]+/],
		// Support whiteboard internal url after spa transition
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/whiteboard\/[a-f0-9-]+/],
		[PageContextType.JIRA_BACKLOG, /\/jira\/.*\/backlog$/],
		[PageContextType.JIRA_BOARD, /\/jira\/.*\/boards\//],

		[PageContextType.JIRA_ISSUE_VIEW, /\/browse\/[^\/]+$/],
		[PageContextType.JIRA_ISSUE_VIEW, /\/jira\/.+\/projects\/.+\?selectedIssue/],
		[PageContextType.JIRA_HOME, /\/jira\/dashboards\/last-visited$/],
		[
			PageContextType.JIRA_SERVICE_DESK,
			/\/jira(?:\/.+)?\/servicedesk\/projects\/.+\/queues\/issue\//,
		],
		[PageContextType.JIRA_PROJECT, /\/jira\/.+\/projects\//],
		...(fg('rovo_chat_jira_plans_minion')
			? [[PageContextType.JIRA_PLAN, /\/jira\/plans\/\d+\//] as [PageContextType, RegExp]]
			: []),
		[PageContextType.JIRA_HOME, /\/jira\/dashboards\/last-visited$/],
		...(fg('permissions_ai_rovo_nudge_and_context')
			? [
					[
						PageContextType.JIRA_CMP_SPACE_SETTINGS_PEOPLE,
						/\/plugins\/servlet\/project-config\/[^\/]+\/(people|permissions)/,
					] as [PageContextType, RegExp],
					[
						PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_VIEW,
						/\/secure\/admin\/ViewPermissionSchemes([!%]?\w*)\.jspa/,
					] as [PageContextType, RegExp],
					[
						PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_ADD,
						/\/secure\/admin\/AddPermissionScheme!default\.jspa/,
					] as [PageContextType, RegExp],
					[
						PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_EDIT,
						/\/(?:jira\/settings\/issues\/permission-schemes|secure\/admin\/EditPermissions!default\.jspa)/,
					] as [PageContextType, RegExp],
					[
						PageContextType.JIRA_ADMIN_PERMISSION_SCHEMES_DELETE,
						/\/secure\/admin\/DeletePermissionScheme!default\.jspa/,
					] as [PageContextType, RegExp],
				]
			: []),
		// atlas home is either home.atlassian.com, home.stg.atlassian.com, team.atlassian.com, or team.stg.atlassian.com
		[PageContextType.ATLAS_HOME, /\/(home|team)\./],
		[PageContextType.ATLAS_PROJECT, /\/(home|team).+\/project\/ATLAS-/],
		[PageContextType.ATLAS_GOAL, /\/(home|team).+\/goal\/ATLAS-/],
		// admin hub: admin.atlassian.com (production) or admin.stg.atlassian.com (staging)
		[PageContextType.ADMIN_HUB, /admin(\.stg)?\.atlassian\.com/],
		[PageContextType.FEEDBACK_APP, /\/feedback-app/],
		// Trello: trello.com and trellis.coffee (enterprise)
		[PageContextType.TRELLO_CARD, /(?:trello\.com|trellis\.coffee)\/c\/[^/]+/],
		[PageContextType.TRELLO_BOARD, /(?:trello\.com|trellis\.coffee)\/b\/[^/]+/],
	];

	const urlPatterns = allUrlPatterns.filter(([type]) => allowedTypes.has(type));

	for (const [type, pattern] of urlPatterns) {
		const matched = pattern.test(location);
		if (!matched) {
			continue;
		}

		return type;
	}

	return FALLBACK_PAGE_CONTEXT_TYPE[useCase];
};

export const usePageContextType = (useCase: UseCase = UseCase.THINKING_VIEW): PageContextType => {
	const location = useWindowLocation();
	const [pageContextType, setPageContextType] = useState<PageContextType>(
		determinePageType(location, useCase),
	);

	useEffect(() => {
		setPageContextType(determinePageType(location, useCase));
	}, [location, useCase]);

	return pageContextType;
};

export type CurrentEditorContext = {
	type: 'editor_context';
	payload: EditorContextPayloadData;
};

export type CurrentPageContext = {
	type: 'current_page';
	pageContextType: PageContextType;
};

export type None = {
	type: 'none';
};

export type CurrentPageContextType = CurrentEditorContext | CurrentPageContext | None;

export const useCurrentPageAsContext = (): { canUseCurrentPageContextUserPreference: (key: string) => boolean; setUseCurrentPageContextUserPreference: (key: string, isEnabled: boolean) => void; currentContext: CurrentPageContextType; } => {
	const pageContextType = usePageContextType();
	const { getChatMode, setUseCurrentPageContext } = useChatModeStoreActions();
	const [editorContext] = useChatContextEditorContext();
	let currentContext: CurrentPageContextType = {
		type: 'none',
	};

	if (editorContext) {
		// prefer editor context over page context if available
		currentContext = {
			type: 'editor_context',
			payload: editorContext,
		};
	} else if (pageContextType !== PageContextType.UNKNOWN) {
		// if no editor context, use page context if available
		currentContext = {
			type: 'current_page',
			pageContextType,
		};
	}

	return {
		canUseCurrentPageContextUserPreference: (key: string): boolean => {
			return getChatMode(key).useCurrentPageContext;
		},
		setUseCurrentPageContextUserPreference: setUseCurrentPageContext,
		currentContext,
	};
};
